import { APP_NAME, type RecoverResult } from '@eventping/shared'
import { Link } from '@tanstack/react-router'
import { MailCheck } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Card, FieldError, Footer, Page, Wordmark } from '@/components/shell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, useMe, useTexts } from '@/lib/api'

/**
 * "Lost your link?" (/recover): no sign-in needed. The answer is the same whether or not the
 * address follows anything, so the page cannot be used to find out who follows what.
 */
export function RecoverPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const smsNumber = useMe().data?.smsNumber ?? null
  const texts = useTexts()

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api<RecoverResult>('/api/recover', { body: { email } })
      setDone(result.message)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page
      header={
        <Link to="/" aria-label={`${APP_NAME} home`}>
          <Wordmark />
        </Link>
      }
    >
      <Card>
        {done ? (
          <div className="text-center" role="status">
            <MailCheck className="mx-auto mb-3 size-10 text-done" aria-hidden />
            <h1 className="text-xl font-semibold">Check your email</h1>
            <p className="mt-2 text-muted-foreground">{done}</p>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <div>
              <h1 className="text-xl font-semibold">Lost your link?</h1>
              <p className="mt-1 text-muted-foreground">
                Enter the email address you signed up with. We’ll email you a fresh link to each
                event you follow.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="recover-email">Email</Label>
              <Input
                id="recover-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <FieldError>{error}</FieldError>
            <Button type="submit" size="lg" className="w-full" disabled={busy}>
              Email me my links
            </Button>
            {texts && (
              <p className="text-sm text-muted-foreground" data-testid="link-hint">
                Signed up with only a phone number?{' '}
                {smsNumber ? (
                  <>
                    Text <strong className="font-semibold text-foreground">LINK</strong> to{' '}
                    <a href={`sms:+1${smsNumber.replace(/\D/g, '')}`} className="underline">
                      {smsNumber}
                    </a>{' '}
                    for a fresh link.
                  </>
                ) : (
                  <>
                    Text <strong className="font-semibold text-foreground">LINK</strong> to the
                    number your updates come from for a fresh link.
                  </>
                )}
              </p>
            )}
          </form>
        )}
      </Card>
      <Footer />
    </Page>
  )
}
